import type { Column } from "@v1/components/designSystem/table/Table";
import type {
  AnalyticsQuery,
  MetricName,
  RangeResult,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { successToast } from "@v1/components/designSystem/alert/Toast";
import Table from "@v1/components/designSystem/table/Table";
import { calculateDelta } from "@v1/features/analytics/analytics";
import { AnalyticsEmptyState } from "@v1/features/analytics/AnalyticsEmptyState";
import AnalyticsFilters from "@v1/features/analytics/AnalyticsFilters";
import { OVERVIEW_TABLE_METRICS } from "@v1/features/analytics/constants";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import MetricWithDelta from "@v1/features/analytics/MetricWithDelta";
import { TablePageCellContent } from "@v1/features/analytics/TablePageCellContent";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import { RESULTS_PER_PAGE } from "@v2/features/analytics/constants";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useSearchParams } from "react-router-dom";
import { useInterval } from "replo-utils/react/use-interval";

const FIRST_TIME_ON_ANALYTICS_PARAM = "firstTimeOnAnalytics";
const PAGE_NAME_COLUMN_WIDTH_CLASSNAME =
  "w-[300px] max-w-[300px] min-w-[300px]";

type AnalyticsTableRow = {
  key: string;
  urlPath: string;
  metadata: RangeResult["metadata"];
  mainRangeResultMetrics: RangeResult["metrics"];
  compareAtRangeResultMetrics: RangeResult["metrics"];
  shouldBlur: boolean;
};

export const AnalyticsOverview: React.FC = () => {
  const logEvent = useLogAnalytics();

  const { query, dispatchAnalyticsQuery, workspaceId, projectId } =
    useAnalyticsQueryContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO (Ryan, 2025-08-19): This needs cleaned up, we should be using a route to satisfy this
  React.useEffect(() => {
    dispatchAnalyticsQuery({
      type: "initOverviewPage",
    });
  }, []);

  const searchTerm = React.useMemo(() => {
    const searchTerm =
      query.filters.urlPath.find(
        (filterCondition) => filterCondition.operator === "contains",
      )?.value ?? "";

    return searchTerm;
  }, [query.filters.urlPath]);

  const handleSearchChange = (value: string) => {
    logEvent("analytics.search", {
      workspaceId,
      projectId,
      query: value,
    });
    dispatchAnalyticsQuery({
      type: "filters.updateUrlPathSearch",
      payload: value,
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const firstTimeOnAnalytics =
    searchParams.get(FIRST_TIME_ON_ANALYTICS_PARAM) !== null;

  const {
    loadedButNoDataForWorkspace,
    mainRangeResults,
    compareAtRangeResults,
    totalRowsCount,
    isLoading,
    refetch: refetchAnalyticsQuery,
  } = useBasicAnalyticsRead({
    type: "withUrlSyncQuery",
    queryConfig: {
      useChartInterval: false,
    },
  });

  /**
   * NOTE Ben 2024-11-01: When we first load analytics after an install, there will be no data for
   * at least a few seconds. In this case, we want to do refetch on the analytics data every
   * 10 seconds. If we've loaded before though, or there's just no query data, we don't want to
   * do this, in which case we pass `null` to the `useInterval` which makes it skip.
   */
  useInterval(
    () => {
      void refetchAnalyticsQuery();
    },
    loadedButNoDataForWorkspace ? 10_000 : null,
  );

  // NOTE (Max, 2024-08-26): pageNumber is the value we show to the users. It starts
  // from 1.
  const currentPageNumber = query.offset / RESULTS_PER_PAGE + 1;

  const resultsPerPage = RESULTS_PER_PAGE;

  const handleOnPageNumberChange = (clickedPageNumber: number) => {
    if (clickedPageNumber !== currentPageNumber) {
      const offset = (clickedPageNumber - 1) * resultsPerPage;
      dispatchAnalyticsQuery({
        type: "updatePagination",
        payload: offset,
      });
    }
  };

  const handleMetricSortClick = (
    sortMetric: MetricName,
    sortOrder: AnalyticsQuery["order"],
  ) => {
    logEvent("analytics.table.sort", {
      workspaceId,
      projectId,
      sortBy: sortMetric,
    });
    dispatchAnalyticsQuery({
      type: "sortMetric",
      payload: {
        sortKey: sortMetric,
        sortOrder,
      },
    });
  };

  /**
   * When clicking on the page, we generate a new path to the deep-dive page
   * with the update urlPathFilters to be EQUAL to whatever page's urlPath just
   * got clicked.
   *
   * We don't need to updateUrlSyncedQuery() here, as we're redirectig to the path
   * which contains these filters. Then, in AnalyticsContext.tsx, the urlSyncedQuery
   * will be udpated anyways, as it gets updated whenever searchParams change (which it
   * will, as we're generating/redirecting to a new path here).
   *
   * @author Max 2024-12-16
   */
  const handlePageTitleClick = React.useCallback(
    (urlPath: string) => {
      dispatchAnalyticsQuery({
        type: "openDeepDivePageFromOverviewPage",
        payload: {
          urlPath,
          source: "analytics_overview",
        },
      });
    },
    [dispatchAnalyticsQuery],
  );

  const { dataDisplayType } = useAnalyticsStatus();

  const tableData: AnalyticsTableRow[] = React.useMemo(() => {
    return mainRangeResults.map((mainRangeResult, index) => {
      const {
        urlPath,
        metadata,
        metrics: mainRangeResultMetrics,
      } = mainRangeResult;

      const compareAtRangeResult = compareAtRangeResults.find(
        (rangeResult) => rangeResult.urlPath === urlPath,
      );

      const compareAtRangeResultMetrics = compareAtRangeResult?.metrics ?? {};
      const shouldBlur = dataDisplayType === "partialData" && index > 2;

      return {
        key: metadata.url,
        urlPath,
        metadata,
        mainRangeResultMetrics,
        compareAtRangeResultMetrics,
        shouldBlur,
      };
    });
  }, [mainRangeResults, compareAtRangeResults, dataDisplayType]);

  const handleUrlPathClick = React.useCallback(() => {
    logEvent("analytics.url.open", {
      source: "analytics_overview",
    });
  }, [logEvent]);

  const analyticsColumns: Column<AnalyticsTableRow>[] = React.useMemo(() => {
    const columns: Column<AnalyticsTableRow>[] = [
      {
        key: "page",
        header: "Page Name",
        width: PAGE_NAME_COLUMN_WIDTH_CLASSNAME,
        renderCell: (row) => (
          <div className="overflow-hidden">
            <TablePageCellContent
              title={row.metadata.title}
              url={row.metadata.url}
              urlPath={row.urlPath}
              onPageTitleClick={() => handlePageTitleClick(row.urlPath)}
              onUrlPathClick={handleUrlPathClick}
              className="truncate"
            />
          </div>
        ),
      },
    ];

    OVERVIEW_TABLE_METRICS.forEach((metric) => {
      columns.push({
        key: metric.key,
        header: metric.label,
        width: "150px",
        sortable: true,
        sortKey: metric.key,
        sortTooltip: `Sort by ${metric.label}`,
        renderCell: (row) => {
          const mainRangeResultCurrentMetricArray =
            row.mainRangeResultMetrics[metric.key as MetricName] ?? [];
          const compareAtRangeResultCurrentMetricArray =
            row.compareAtRangeResultMetrics[metric.key as MetricName] ?? [];

          const compareAtRangeViewsArray =
            row.compareAtRangeResultMetrics.views ?? [];
          const doesCompareValueExist = (compareAtRangeViewsArray[0] ?? 0) > 0;

          return (
            <MetricWithDelta
              name={metric.key}
              value={mainRangeResultCurrentMetricArray[0] ?? 0}
              delta={calculateDelta(
                mainRangeResultCurrentMetricArray[0],
                compareAtRangeResultCurrentMetricArray[0],
              )}
              doesCompareValueExist={doesCompareValueExist}
              wrapperClassName="justify-start"
              valueClassName="text-xs"
            />
          );
        },
      });
    });

    return columns;
  }, [handlePageTitleClick, handleUrlPathClick]);

  React.useEffect(() => {
    if (firstTimeOnAnalytics) {
      successToast(
        "Analytics Connected",
        "Your store has been connected and tracking has started!",
      );
      setSearchParams(
        (params) => {
          params.delete(FIRST_TIME_ON_ANALYTICS_PARAM);
          return new URLSearchParams(params);
        },
        { replace: true },
      );
    }
  }, [firstTimeOnAnalytics, setSearchParams]);

  const sort = {
    sortKey: query.sortMetric,
    sortOrder: query.order,
    onSortChange: (sortKey: string, sortOrder: AnalyticsQuery["order"]) => {
      handleMetricSortClick(sortKey as MetricName, sortOrder);
    },
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Filters */}
      <div className="sticky top-0 bg-white z-10 pb-4 border-b border-border shrink-0">
        <AnalyticsFilters
          search={{
            value: searchTerm,
            onChange: handleSearchChange,
          }}
        />
      </div>

      {/* Table with height constraint to always show pagination */}
      <div className="flex-1 min-h-0 pt-4">
        <Table
          data={tableData}
          columns={analyticsColumns}
          isLoading={isLoading}
          loadingRows={Math.min(resultsPerPage, 10)}
          emptyState={
            <AnalyticsEmptyState
              type={loadedButNoDataForWorkspace ? "noTraffic" : "resetFilters"}
            />
          }
          sort={sort}
          pagination={{
            currentPage: currentPageNumber,
            totalRows: totalRowsCount ?? 0,
            pageSize: resultsPerPage,
            onPageChange: handleOnPageNumberChange,
          }}
          getRowClassName={(row) => (row.shouldBlur ? "blur-xs" : undefined)}
          size="sm"
          variant="bordered"
          scrollable={true}
          maxBodyHeight="calc(90vh - 250px)"
        />
      </div>
    </div>
  );
};
